"use client";

import Button from "@/components/Elements/Button";
import TableAccordion from "@/components/Elements/TableAccordion";
import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import useSWR, { mutate } from "swr";
import useUserStore from "@/stores/useUserStore";
import {
  getUserInvoicePdf,
  getUserInvoices,
  payInvoices,
} from "@/actions/user";
import Skeleton from "react-loading-skeleton";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { base64StringToBlob } from "blob-util";
import toast from "react-hot-toast";
import Modal from "@/components/Modal/Modal";
import Iframe from "@/components/Iframe";
import NotFoundText from "@/components/NotFoundText";
import { getErrorCodeMessage } from "@/lib/reporting";
import { useShallow } from "zustand/react/shallow";
import CustomPagination from "../../../components/CustomPagination";
import useCustomPagination from "@/hooks/useCustomPagination";
import { formatPrice, parseToDate } from "@/lib/utils";
import ModalElement from "@/components/Modal/Modal";
import "react-loading-skeleton/dist/skeleton.css";
import { MD5 } from "crypto-js";

const Invoices = ({ block, blockStyle, extraClass }: BlockProps) => {
  const { user, setFetchKeys, fetchKeys } = useUserStore(
    useShallow((state) => state)
  );

  const userId = user?.userId;

  const [paginationArgsPaid, setPaginationArgsPaid] = useState<any>({
    page: 1,
    size: 10,
    total: 0,
  });

  const [paginationArgsUnpaid, setPaginationArgsUnpaid] = useState<any>({
    page: 1,
    size: 10,
    total: 0,
  });

  const [displayedPaidItems, setDisplayedPaidItems] = useState<any>([]);
  const [displayedUnpaidItems, setDisplayedUnpaidItems] = useState<any>([]);

  const [payModal, setPayModal] = useState({
    isOpen: false,
    content: "",
  });

  const invoiceFetchKey =
    fetchKeys?.userInvoices || MD5(`userInvoices-${userId}`).toString();

  const modalIsOpen = payModal?.isOpen;

  const [selectedRowsUnpaid, setSelectedRowsUnpaid] = useState([]);
  const [selectedRowsPaid, setSelectedRowsPaid] = useState([]);

  const numSelectedRowsPaid = selectedRowsPaid.length;
  const numSelectedRowsUnpaid = selectedRowsUnpaid.length;

  const totalValueMarkedRows = selectedRowsUnpaid.reduce(
    (acc: number, row: any) => {
      const amount = row?.values?.invoiceLeftAmout
        ? row?.values?.invoiceLeftAmout
        : row?.values?.invoiceLeftAmount || 0;

      return acc + amount;
    },
    0
  );

  const userInvoicesFetchKey = ["api/getUserInvoices", userId, invoiceFetchKey];

  const { data: userInvoices, isLoading } = useSWR<Invoice[], Error>(
    userInvoicesFetchKey,
    async () => {
      const getInvoices = await getUserInvoices(invoiceFetchKey);

      const paid = getInvoices?.filter(
        (invoice) => invoice.invoiceStatus === "paid"
      );

      const unpaid = getInvoices?.filter(
        (invoice) =>
          invoice.invoiceStatus === "unpaid" ||
          invoice.invoiceStatus === "partpaid"
      );

      setPaginationArgsPaid((prev: any) => ({
        ...prev,
        total: paid?.length,
      }));

      setPaginationArgsUnpaid((prev: any) => ({
        ...prev,
        total: unpaid?.length,
      }));

      return getInvoices;
    },
    {
      refreshInterval: 10000,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  const getInvoiceStatusText = (invoice: any) => {
    const status = invoice?.invoiceStatus;
    const amount = invoice?.invoiceLeftAmount;

    if (["unpaid", "partpaid"].includes(status) && amount < 0) {
      return "Outnyttjad";
    }

    if (status == "paid" && amount < 0) {
      return "Utnyttjad";
    }

    switch (status) {
      case "unpaid":
        return "Obetald";
      case "paid":
        return "Betald";
      case "partpaid":
        return "Delbetald";
      case "pending":
        return "Väntande";
      default:
        return "Ohanterad";
    }
  };

  const renderInvoiceNo = (invoice: Invoice) => {
    const fortnoxNo = invoice.invoiceFortnoxDocumentNumber;
    if (fortnoxNo) {
      return `${fortnoxNo}`;
    }
    return invoice.invoiceNo;
  };

  const mapInvoiceNames = (invoice: Invoice) => {
    const date = parseToDate(invoice.invoiceDate);

    const formattedDate = isNaN(date.getTime())
      ? ""
      : new Intl.DateTimeFormat("sv-SE", {
          year: "numeric",
          month: "long",
          day: "2-digit",
        }).format(date);

    const isPaid = invoice.invoiceStatus === "paid";
    const totalLeft = isPaid
      ? invoice?.invoiceTotalAmount || 0
      : invoice?.invoiceLeftAmount || 0;

    const statusText = invoice.invoiceFortnoxSyncWarning ? (
      <div>
        {getInvoiceStatusText(invoice)}
        <div className="text-xs text-amber-600 mt-0.5">⚠ Kunde inte hämta just nu, försök igen senare</div>
      </div>
    ) : getInvoiceStatusText(invoice);

    return {
      invoiceId: invoice.invoiceId,
      invoiceNo: renderInvoiceNo(invoice),
      invoiceNoRaw: invoice.invoiceNo,
      invoiceDate: formattedDate,
      invoiceLeftAmountFormatted: `${formatPrice(totalLeft)}`,
      invoiceLeftAmount: totalLeft,
      invoiceStatus: invoice.invoiceStatus,
      invoiceStatusText: statusText,
    };
  };

  const syncWarningIds = useMemo(
    () =>
      (userInvoices ?? [])
        .filter((i) => i.invoiceFortnoxSyncWarning)
        .map((i) => i.invoiceId),
    [userInvoices]
  );

  const paidItems = useMemo(
    () =>
      userInvoices
        ?.filter((invoice) => invoice.invoiceStatus === "paid")
        .map((invoice) => ({
          ...mapInvoiceNames(invoice),
          action: invoice.invoiceFortnoxSyncWarning ? null : (
            <div className="flex flex-row space-x-4">
              <a
                className="text-black text-sm border px-3 py-2 rounded-lg border-gray-300 cursor-pointer hover:bg-blue hover:text-white w-fit px-0 flex items-center !no-underline"
                href={`/api/invoice/${invoice.invoiceId}/pdf`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Visa
              </a>
            </div>
          ),
        })) || [],
    [userInvoices]
  );

  const unpaidItems = useMemo(() => {
    return (
      userInvoices
        ?.filter(
          (invoice) =>
            invoice.invoiceStatus === "unpaid" ||
            invoice.invoiceStatus === "partpaid"
        )
        .map((invoice) => ({
          ...mapInvoiceNames(invoice),
          action: invoice.invoiceFortnoxSyncWarning ? null : (
            <div className="flex flex-row space-x-4">
              <a
                className="text-black text-sm border px-3 py-2 rounded-lg border-gray-300 cursor-pointer hover:bg-blue hover:text-white w-fit px-0 flex items-center !no-underline"
                href={`/api/invoice/${invoice.invoiceId}/pdf`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Visa
              </a>
              <button
                className="bg-blue text-white text-sm border border-blue px-3 py-2 rounded-lg cursor-pointer hover:opacity-80 w-fit"
                onClick={() => paySingleInvoiceHandler(invoice.invoiceId)}
              >
                Betala
              </button>
            </div>
          ),
        })) || []
    );
  }, [userInvoices]);

  useCustomPagination({
    paginationArg: paginationArgsPaid,
    allItems: paidItems || [],
    setItems: setDisplayedPaidItems,
  });

  useCustomPagination({
    paginationArg: paginationArgsUnpaid,
    allItems: unpaidItems || [],
    setItems: setDisplayedUnpaidItems,
  });

  useEffect(() => {
    mutate(userInvoicesFetchKey);
  }, [modalIsOpen]);

  const downloadInvoices = async (rows: any) => {
    const zip = new JSZip();
    let errors = [] as any;

    const downloadInvoicesPromise = async (rows: any, zip: JSZip) => {
      for (let row of rows) {
        const invoiceNo = row?.values?.invoiceNoRaw ?? row?.values?.invoiceNo;

        const getInvoice =
          userInvoices && userInvoices.find
            ? userInvoices.find((invoice) => invoice.invoiceNo === invoiceNo)
            : null;

        if (!getInvoice) {
          toast.error(`Kunde inte hitta faktura ${invoiceNo}`);
          return;
        }

        const invoiceId = getInvoice.invoiceId;
        const ret = await getUserInvoicePdf(Number(invoiceId));

        if (ret) {
          const pdfBlob = base64StringToBlob(ret, "application/pdf");
          zip.file(`Faktura_${invoiceNo}.pdf`, pdfBlob);
        } else {
          errors.push(invoiceNo);
        }
      }

      if (errors.length) {
        throw new Error(`Misslyckades med att ladda ner fakturor`);
      }

      const genZip = await zip.generateAsync({ type: "blob" });

      saveAs(genZip, "fakturor.zip");
    };

    toast.promise(downloadInvoicesPromise(rows, zip), {
      loading: "Laddar ner fakturor...",
      success: "Fakturor nerladdade!",
      error: (err) => `Misslyckades med att ladda ner fakturor`,
    });
  };

  const columns = [
    {
      name: "Faktura",
      accessor: "invoiceNo",
    },
    {
      name: "Datum",
      accessor: "invoiceDate",
    },
    {
      name: "Total",
      accessor: "invoiceLeftAmountFormatted",
    },
    {
      name: "Status",
      accessor: "invoiceStatusText",
    },
    {
      name: "Åtgärd",
      accessor: "action",
    },
  ];

  const paySingleInvoiceHandler = async (invoiceId: number) => {
    try {
      const response = await payInvoices([invoiceId]);
      const embed = response?.data?.embed;

      if (!embed) {
        toast.error(
          getErrorCodeMessage(
            response?.error?.code,
            null,
            "Misslyckades med att hämta betalningsformulär, försök igen senare. (1)"
          )
        );
        return;
      }

      setPayModal({ isOpen: true, content: embed });
    } catch (error) {
      toast.error(
        "Misslyckades med att hämta betalningsformulär, försök igen senare. (2)"
      );
    }
  };

  const payIvoicesHandler = async () => {
    if (!selectedRowsUnpaid || !userInvoices) return;

    const invoiceIds = selectedRowsUnpaid.reduce((acc: number[], row: any) => {
      const invoiceNo = row?.values?.invoiceNoRaw ?? row?.values?.invoiceNo;

      const getInvoice = userInvoices.find
        ? userInvoices.find((invoice) => invoice.invoiceNo === invoiceNo)
        : null;

      if (!getInvoice) {
        return acc;
      }

      return [...acc, getInvoice?.invoiceId];
    }, []);

    try {
      const response = await payInvoices(invoiceIds);
      const embed = response?.data?.embed;

      if (!embed) {
        toast.error(
          getErrorCodeMessage(
            response?.error?.code,
            null,
            "Misslyckades med att hämta betalningsformulär, försök igen senare. (1)"
          )
        );
        return;
      }

      setPayModal({
        isOpen: true,
        content: embed,
      });
    } catch (error) {
      toast.error(
        "Misslyckades med att hämta betalningsformulär, försök igen senare. (2)"
      );
    }
  };

  const closePayModal = () => {
    setFetchKeys({
      userInvoices: MD5(
        `userInvoices-${Math.random().toString(36).substring(7)}-${userId}`
      ).toString(),
    });

    setSelectedRowsPaid([]);
    setSelectedRowsUnpaid([]);

    setPayModal({
      isOpen: false,
      content: "",
    });
  };

  // Memoized content
  const memoizedContent = useMemo(() => payModal?.content, [payModal?.content]);

  // Memoized Modal
  const memoizedModal = useMemo(() => {
    return (
      <ModalElement
        isOpen={payModal?.isOpen}
        size="xl"
        modalBg="aliceblue"
        wrapperExtraClass="!p-0 scrollbar-hide !shadow-none"
        useFloatingHeader
        modalBody={
          <div className="h-[800px] lg:h-[750px] p-6 md:p-6 md:py-12 bg-white rounded-lg">
            <Iframe content={memoizedContent} />
          </div>
        }
        onClose={closePayModal}
      />
    );
  }, [payModal?.isOpen, memoizedContent]);

  return (
    <>
      {memoizedModal}

      <div className="flex flex-col space-y-6 w-full" style={blockStyle}>
        <div className="flex flex-col sm:space-y-4">
          <div className="flex flex-col">
            <div className="flex flex-col sm:flex-row justify-between md:items-center my-6 space-y-6 sm:space-y-0">
              <h3 className="text-lg md:text-5xl">Obetalda fakturor</h3>

              <div className="flex flex-row space-x-4">
                <Button
                  className="text-blue border-blue hover:bg-blue hover:text-white w-fit"
                  bgColor="transparent"
                  icon={"download"}
                  onClick={() => downloadInvoices(selectedRowsUnpaid)}
                  disabled={!numSelectedRowsUnpaid}
                >
                  Ladda ner markerade{" "}
                  {numSelectedRowsUnpaid ? `(${numSelectedRowsUnpaid})` : ""}
                </Button>

                <Button
                  className="text-blue border-blue hover:bg-blue hover:text-white border-2 !border-solid w-fit"
                  bgColor="transparent"
                  onClick={payIvoicesHandler}
                  disabled={!numSelectedRowsUnpaid || totalValueMarkedRows <= 0}
                >
                  Betala markerade{" "}
                  {numSelectedRowsUnpaid ? `(${numSelectedRowsUnpaid})` : ""}
                </Button>
              </div>
            </div>

            {isLoading ? (
              <Skeleton />
            ) : !displayedUnpaidItems?.length ? (
              <NotFoundText
                title="Inga fakturor"
                description="Det finns inga obetalda fakturor att lista."
              />
            ) : (
              <>
                <TableAccordion
                  columns={columns}
                  items={displayedUnpaidItems}
                  useCheckbox
                  setSelectedRows={setSelectedRowsUnpaid}
                  defaultChecked={false}
                  nonSelectableIds={syncWarningIds}
                />

                <CustomPagination
                  paginationArgs={paginationArgsUnpaid}
                  setPaginationArgs={paginationArgsUnpaid}
                />
              </>
            )}
          </div>

          <div className="flex flex-col">
            <div className="flex flex-col md:flex-row justify-between md:items-center my-6 space-y-6 sm:space-y-0">
              <h3 className="text-lg md:text-5xl">Betalda fakturor</h3>
              {!!numSelectedRowsPaid && (
                <Button
                  className={classNames(
                    "text-blue border-blue hover:bg-blue hover:text-white w-fit !opacity-0 pointer-events-none",
                    {
                      "!opacity-100 pointer-events-auto": numSelectedRowsPaid,
                    }
                  )}
                  bgColor="transparent"
                  icon="download"
                  onClick={() => downloadInvoices(selectedRowsPaid)}
                >
                  Ladda ner markerade{" "}
                  {numSelectedRowsPaid ? `(${numSelectedRowsPaid})` : ""}
                </Button>
              )}
            </div>

            {isLoading ? (
              <Skeleton />
            ) : !displayedPaidItems?.length ? (
              <NotFoundText
                title="Inga fakturor"
                description="Det finns inga betalda fakturor att lista."
              />
            ) : (
              <>
                <TableAccordion
                  columns={columns}
                  items={displayedPaidItems}
                  setSelectedRows={setSelectedRowsPaid}
                  useCheckbox
                  showRowForSingleDownload
                  nonSelectableIds={syncWarningIds}
                />

                <CustomPagination
                  paginationArgs={paginationArgsPaid}
                  setPaginationArgs={setPaginationArgsPaid}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Invoices;
